import { useEffect, useState, useRef } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useGenerateImageFromBrandProfile } from 'hooks/queries/brandProfiles/useGenerateImageFromBrandProfile'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { SelectedImage, SuggestionRequestStatus } from 'types'
import { isCancelledError } from 'react-query'
import { reportError } from 'utils/error'

interface UseBlazeImageGenerationProps {
  brandProfileId: number
}

interface UseBlazeImageGenerationResult {
  image: SelectedImage | null
  isGenerating: boolean
  error: Error | null
}

export const useBlazeImageGeneration = ({
  brandProfileId,
}: UseBlazeImageGenerationProps): UseBlazeImageGenerationResult => {
  const workspaceId = useCurrentWorkspaceId()
  const [requestId, setRequestId] = useState<number>()
  const [image, setImage] = useState<SelectedImage | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const hasTriggeredRef = useRef(false)

  const generateImageMutation = useGenerateImageFromBrandProfile({
    workspaceId,
    brandProfileId,
    generationType: 'stock_image',
  })

  const { data } = usePollAISuggestionRequest({ id: requestId ?? 0 }, { enabled: !!requestId, intervalMs: 1000 })
  const request = data?.request

  useEffect(() => {
    if (hasTriggeredRef.current) return

    hasTriggeredRef.current = true
    const controller = new AbortController()

    generateImageMutation
      .mutateAsync({ signal: controller.signal })
      .then((response) => setRequestId(response.request.id))
      .catch((err) => {
        if (!isCancelledError(err)) {
          reportError(err)
          setError(err)
        }
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!request) return

    if (request.status === SuggestionRequestStatus.Successful && request.generated_content) {
      try {
        const content = JSON.parse(request.generated_content)
        if (!content?.brand_image_generation) {
          throw new Error('Invalid response format: missing brand_image_generation')
        }
        setImage(content.brand_image_generation)
      } catch (parseError) {
        reportError(parseError)
        setError(parseError as Error)
      }
    } else if (
      request.status === SuggestionRequestStatus.Failed ||
      request.status === SuggestionRequestStatus.Expired
    ) {
      const err = new Error(`Generation failed: ${request.status}`)
      setError(err)
    }
  }, [request])

  return {
    image,
    isGenerating: !image && !error,
    error,
  }
}
